import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  ChakraProvider, 
  extendTheme, 
  ColorModeScript,
  useColorMode,
  IconButton,
  Box
} from '@chakra-ui/react';
import { MoonIcon, SunIcon } from '@chakra-ui/icons';

// Theme Context
const ThemeContext = createContext();

// Custom theme configuration
const customTheme = extendTheme({
  config: {
    initialColorMode: 'dark',
    useSystemColorMode: false,
  },
  colors: {
    brand: {
      50: '#e6f7ff',
      100: '#bae7ff',
      200: '#91d5ff',
      300: '#69c0ff',
      400: '#40a9ff',
      500: '#0099cc', // Primary brand color
      600: '#0088b3',
      700: '#007799',
      800: '#006680',
      900: '#004d66',
    },
    glass: {
      surface: 'rgba(255, 255, 255, 0.1)',
      elevated: 'rgba(255, 255, 255, 0.15)',
      border: 'rgba(255, 255, 255, 0.2)',
      shadow: 'rgba(0, 0, 0, 0.3)',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#E5E5E5',
      muted: '#B8B8B8',
    },
    background: {
      primary: 'transparent',
      secondary: 'rgba(255, 255, 255, 0.05)',
    }
  },
  fonts: {
    heading: 'Inter, sans-serif',
    body: 'Inter, sans-serif',
  },
  styles: {
    global: (props) => ({
      'html, body': {
        bg: '#1a1a2e',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        color: '#FFFFFF',
        fontFamily: 'body',
        minHeight: '100vh',
      },
      '#root': {
        minHeight: '100vh',
      },
      '*': {
        borderColor: 'rgba(255, 255, 255, 0.2)',
      },
    }),
  },
  components: {
    // Card component
    Card: {
      baseStyle: {
        container: {
          bg: 'glass.surface',
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: 'glass.border',
          borderRadius: 'xl',
          boxShadow: '0 8px 32px glass.shadow',
          color: 'text.primary',
        },
        header: {
          bg: 'transparent',
          color: 'text.primary',
        },
        body: {
          bg: 'transparent',
          color: 'text.primary',
        },
        footer: {
          bg: 'transparent',
          color: 'text.primary',
        },
      },
    },
    // Table component
    Table: {
      baseStyle: {
        table: {
          bg: 'transparent',
          color: 'text.primary',
        },
        thead: {
          bg: 'glass.elevated',
          th: {
            bg: 'glass.elevated',
            color: 'text.primary',
            borderColor: 'glass.border',
            fontWeight: 'bold',
          },
        },
        tbody: {
          tr: {
            bg: 'transparent',
            _hover: {
              bg: 'glass.elevated',
            },
            td: {
              bg: 'transparent',
              color: 'text.primary',
              borderColor: 'glass.border',
            },
          },
        },
      },
    },
    // Modal component
    Modal: {
      baseStyle: {
        dialog: {
          bg: 'glass.surface',
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: 'glass.border',
          boxShadow: '0 8px 32px glass.shadow',
          color: 'text.primary',
        },
        header: {
          bg: 'transparent',
          color: 'text.primary',
          borderColor: 'glass.border',
        },
        body: {
          bg: 'transparent',
          color: 'text.primary',
        },
        footer: {
          bg: 'transparent',
          borderColor: 'glass.border',
        },
      },
    },
    // Button component
    Button: {
      baseStyle: {
        bg: 'glass.surface',
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'glass.border',
        backdropFilter: 'blur(10px)',
        _hover: {
          bg: 'glass.elevated',
          borderColor: 'brand.500',
          _disabled: {
            bg: 'glass.surface',
            borderColor: 'glass.border',
          },
        },
        _active: {
          bg: 'glass.elevated',
        },
        _disabled: {
          bg: 'glass.surface',
          color: 'text.muted',
          borderColor: 'glass.border',
        },
      },
      variants: {
        solid: {
          bg: 'brand.500',
          color: 'white',
          _hover: {
            bg: 'brand.600',
            _disabled: {
              bg: 'brand.500',
            },
          },
        },
        outline: {
          bg: 'transparent',
          borderColor: 'brand.500',
          color: 'brand.500',
          _hover: {
            bg: 'brand.500',
            color: 'white',
          },
        },
        ghost: {
          bg: 'transparent',
          color: 'text.primary',
          _hover: {
            bg: 'glass.elevated',
          },
        },
      },
    },
    // Input component
    Input: {
      baseStyle: {
        field: {
          bg: 'glass.surface',
          border: '1px solid',
          borderColor: 'glass.border',
          color: 'text.primary',
          _placeholder: {
            color: 'text.muted',
          },
          _focus: {
            borderColor: 'brand.500',
            boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
          },
          _hover: {
            bg: 'glass.elevated',
          },
        },
      },
    },
    // Textarea component
    Textarea: {
      baseStyle: {
        bg: 'glass.surface',
        border: '1px solid',
        borderColor: 'glass.border',
        color: 'text.primary',
        _placeholder: {
          color: 'text.muted',
        },
        _focus: {
          borderColor: 'brand.500',
          boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
        },
        _hover: {
          bg: 'glass.elevated',
        },
      },
    },
    // Select component
    Select: {
      baseStyle: {
        field: {
          bg: 'glass.surface',
          border: '1px solid',
          borderColor: 'glass.border',
          color: 'text.primary',
          _focus: {
            borderColor: 'brand.500',
            boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)',
          },
          _hover: {
            bg: 'glass.elevated',
          },
        },
      },
    },
    // Menu component
    Menu: {
      baseStyle: {
        list: {
          bg: 'glass.surface',
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: 'glass.border',
          boxShadow: '0 8px 32px glass.shadow',
          color: 'text.primary',
        },
        item: {
          bg: 'transparent',
          color: 'text.primary',
          _hover: {
            bg: 'glass.elevated',
          },
          _focus: {
            bg: 'glass.elevated',
          },
        },
      },
    },
    // Popover component
    Popover: {
      baseStyle: {
        content: {
          bg: 'glass.surface',
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: 'glass.border',
          boxShadow: '0 8px 32px glass.shadow',
          color: 'text.primary',
        },
        header: {
          bg: 'transparent',
          color: 'text.primary',
          borderColor: 'glass.border',
        },
        body: {
          bg: 'transparent',
          color: 'text.primary',
        },
        footer: {
          bg: 'transparent',
          borderColor: 'glass.border',
        },
      },
    },
    // Drawer component
    Drawer: {
      baseStyle: {
        dialog: {
          bg: 'glass.surface',
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: 'glass.border',
          color: 'text.primary',
        },
        header: {
          bg: 'transparent',
          color: 'text.primary',
          borderColor: 'glass.border',
        },
        body: {
          bg: 'transparent',
          color: 'text.primary',
        },
        footer: {
          bg: 'transparent',
          borderColor: 'glass.border',
        },
      },
    },
    // Alert component
    Alert: {
      baseStyle: {
        container: {
          bg: 'glass.surface',
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: 'glass.border',
          color: 'text.primary',
        },
      },
    },
    // Tooltip component
    Tooltip: {
      baseStyle: {
        bg: 'glass.surface',
        backdropFilter: 'blur(20px)',
        border: '1px solid',
        borderColor: 'glass.border',
        color: 'text.primary',
      },
    },
    // Divider component
    Divider: {
      baseStyle: {
        borderColor: 'glass.border',
      },
    },
    // Text component
    Text: {
      baseStyle: {
        color: 'text.primary',
      },
    },
    // Heading component
    Heading: {
      baseStyle: {
        color: 'text.primary',
      },
    },
    // Box component
    Box: {
      baseStyle: {
        color: 'text.primary',
      },
    },
    // Container component
    Container: {
      baseStyle: {
        color: 'text.primary',
      },
    },
    // Flex component
    Flex: {
      baseStyle: {
        color: 'text.primary',
      },
    },
    // Stack component
    Stack: {
      baseStyle: {
        color: 'text.primary',
      },
    },
    // Badge component
    Badge: {
      baseStyle: {
        bg: 'glass.surface',
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'glass.border',
      },
    },
    // Code component
    Code: {
      baseStyle: {
        bg: 'glass.surface',
        color: 'text.primary',
        border: '1px solid',
        borderColor: 'glass.border',
      },
    },
    // AccordionButton component
    AccordionButton: {
      baseStyle: {
        bg: 'glass.surface',
        color: 'text.primary',
        _hover: {
          bg: 'glass.elevated',
        },
        _expanded: {
          bg: 'glass.elevated',
        },
      },
    },
    // AccordionPanel component
    AccordionPanel: {
      baseStyle: {
        bg: 'transparent',
        color: 'text.primary',
      },
    },
    // Tab component
    Tab: {
      baseStyle: {
        bg: 'transparent',
        color: 'text.secondary',
        _selected: {
          color: 'brand.500',
          borderColor: 'brand.500',
        },
        _hover: {
          color: 'text.primary',
        },
      },
    },
    // TabPanel component
    TabPanel: {
      baseStyle: {
        bg: 'transparent',
        color: 'text.primary',
      },
    },
    // Checkbox component
    Checkbox: {
      baseStyle: {
        control: {
          bg: 'glass.surface',
          border: '1px solid',
          borderColor: 'glass.border',
          color: 'text.primary',
          _checked: {
            bg: 'brand.500',
            borderColor: 'brand.500',
          },
          _focus: {
            boxShadow: '0 0 0 2px var(--chakra-colors-brand-200)',
          },
        },
        label: {
          color: 'text.primary',
        },
      },
    },
    // Radio component
    Radio: {
      baseStyle: {
        control: {
          bg: 'glass.surface',
          border: '1px solid',
          borderColor: 'glass.border',
          color: 'text.primary',
          _checked: {
            bg: 'brand.500',
            borderColor: 'brand.500',
          },
          _focus: {
            boxShadow: '0 0 0 2px var(--chakra-colors-brand-200)',
          },
        },
        label: {
          color: 'text.primary',
        },
      },
    },
    // Switch component
    Switch: {
      baseStyle: {
        track: {
          bg: 'glass.surface',
          border: '1px solid',
          borderColor: 'glass.border',
          _checked: {
            bg: 'brand.500',
          },
        },
        thumb: {
          bg: 'text.primary',
        },
      },
    },
    // Slider component
    Slider: {
      baseStyle: {
        track: {
          bg: 'glass.surface',
          border: '1px solid',
          borderColor: 'glass.border',
        },
        filledTrack: {
          bg: 'brand.500',
        },
        thumb: {
          bg: 'brand.500',
          borderColor: 'brand.500',
        },
      },
    },
    // Progress component
    Progress: {
      baseStyle: {
        track: {
          bg: 'glass.surface',
          border: '1px solid',
          borderColor: 'glass.border',
        },
        filledTrack: {
          bg: 'brand.500',
        },
      },
    },
    // Skeleton component
    Skeleton: {
      baseStyle: {
        background: 'glass.surface',
      },
    },
    // Spinner component
    Spinner: {
      baseStyle: {
        color: 'brand.500',
      },
    },
    // Link component
    Link: {
      baseStyle: {
        color: 'brand.500',
        _hover: {
          color: 'brand.400',
          textDecoration: 'underline',
        },
      },
    },
  },
});

// Theme toggle button component
export const ThemeToggle = ({ size = 'md', ...props }) => {
  const { colorMode, toggleColorMode } = useColorMode();
  
  return (
    <IconButton
      aria-label="Toggle theme"
      icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
      onClick={toggleColorMode}
      variant="ghost"
      size={size}
      {...props}
    />
  );
};

// Custom theme hook
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Force dark mode component
const ForceDarkMode = ({ children }) => {
  const { colorMode, setColorMode } = useColorMode();
  
  // Force dark mode immediately on mount and whenever it changes
  useEffect(() => {
    if (colorMode !== 'dark') {
      setColorMode('dark');
    }
  }, [colorMode, setColorMode]);
  
  // Also force dark mode in localStorage synchronously
  useEffect(() => {
    localStorage.setItem('chakra-ui-color-mode', 'dark');
  }, []);

  return children;
};

// Main theme provider component
const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('dark');
  
  useEffect(() => {
    // Load theme from localStorage
    const savedTheme = localStorage.getItem('chakra-ui-color-mode');
    if (savedTheme) {
      setTheme(savedTheme);
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    localStorage.setItem('chakra-ui-color-mode', newTheme);
  };

  const themeContextValue = {
    theme,
    toggleTheme,
    isDark: theme === 'dark',
    isLight: theme === 'light',
  };

  return (
    <ThemeContext.Provider value={themeContextValue}>
      <ChakraProvider theme={customTheme}>
        <ForceDarkMode>
          <Box minH="100vh" color="text.primary">
            {children}
          </Box>
        </ForceDarkMode>
      </ChakraProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;